import '../App.css';
import {
    Container, Card, Space, Text,
    createStyles, rem, Group, Divider, Badge, Box
} from '@mantine/core';
import Footer from '../components/Footer';
import Head from '../components/Head';
import ReactMarkdown from 'react-markdown';
import { Helmet } from 'react-helmet-async';
import { IconFileText, IconShieldCheck, IconInfoCircle } from '@tabler/icons-react';

const useStyles = createStyles((theme) => ({
    heroWrapper: {
        background: 'radial-gradient(ellipse at 50% 60%, #e6f4ff 0%, #e8f6fb 50%, #f4fbff 100%)',
        paddingTop: rem(40),
        paddingBottom: rem(44),
        textAlign: 'center',
    },

    pageTitle: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 800,
        letterSpacing: '-0.02em',
        fontSize: rem(48),
        color: '#0048a0',

        [theme.fn.smallerThan('md')]: { fontSize: rem(36) },
        [theme.fn.smallerThan('sm')]: { fontSize: rem(26) },
    },

    subtitle: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontSize: rem(18),
        color: theme.colors.gray[6],
        marginTop: rem(8),
        maxWidth: rem(640),
        marginLeft: 'auto',
        marginRight: 'auto',
    },

    card: {
        background: 'rgba(255, 255, 255, 0.95)',
        boxShadow: theme.shadows.md,
        borderRadius: theme.radius.lg,
        border: '1px solid #e0eaf0',
    },

    sectionTitle: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(22),
        color: '#0048a0',
    },

    placeholderBox: {
        backgroundColor: '#f8fafc',
        border: '2px dashed #cbd5e1',
        borderRadius: theme.radius.md,
        padding: rem(28),
        textAlign: 'center',
    },

    prose: {
        fontSize: rem(15),
        lineHeight: 1.7,
        color: theme.colors.gray[8],
        '& p': {
            marginBottom: rem(16),
        },
        '& h3': {
            fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
            color: '#0048a0',
            marginTop: rem(24),
            marginBottom: rem(12),
        },
        '& ul': {
            paddingLeft: rem(20),
            marginBottom: rem(16),
        },
        '& li': {
            marginBottom: rem(6),
        }
    }
}));

const agreementText = `
### 1. Nature of Agreement

This initiative is a commercial talent search and casting process for independent contractor creative services, not a sweepstakes, lottery, or binding contest. Participation and selection do not constitute an offer of employment.

### 2. Eligibility & Content Standards

**Age & Residency:** Open to legal residents of the 50 United States and Washington, D.C., aged 18 years or older at the time of entry.

**Content Guidelines:** All submitted content must not be overly explicit, as evaluated by Paws and Bubbles Spa (PBS) and Comedy Seen. Content must not contain explicit language, hate speech, defamation, or unauthorized third-party intellectual property (including commercial rights to all background audio/music, logos, or imagery).

### 3. Selection Process & Client Discretion

**Stage 1 Selection:** PBS will select three (3) candidates to produce a short-form video (“Reel”) based on a creative prompt provided by PBS.

**Brand Review & Approval:** Prior to public posting, Stage 1 candidates must submit completed video files to PBS for review. PBS reserves the right to request reasonable edits or reject submissions that fail to meet brand identity, audio clearance, or safety standards.

**Audience Feedback & Winner Selection:** Social media comments and engagement metrics (including any follower-weighted formulas) serve as non-binding fan feedback to gauge audience sentiment. PBS retains sole, final discretion to select the Stage 2 partner based on creative execution, brand fit, and production capability.

**Disqualification:** PBS reserves the right to disqualify any applicant suspected of metric manipulation, automated engagement, or unprofessional conduct.

### 4. Deliverables & Contractor Compensation

**Stage 1 Fee:** The three (3) selected Stage 1 participants will each receive a flat contractor fee of $100 upon delivery and PBS approval of their Reel asset.

**Stage 2 Partnership Fee:** One (1) selected partner will receive a flat fee of $450 to participate in one (1) virtual pre-launch brainstorming session and produce three (3) approved promotional assets over a 14-day campaign.

**Tax Status & Payment Terms:** All payments are independent contractor fees. Participants are responsible for all applicable local, state, and federal taxes (Form W-9 required prior to payout). Approved payments will be issued via electronic transfer within 14 business days of final milestone delivery.

### 5. Intellectual Property & Commercial License

**Ownership:** Participants retain baseline ownership of their original comedic persona and scripts.

**Promotional License:** By participating, Participant grants PBS and Comedy Seen an irrevocable, non-exclusive, worldwide, royalty-free, perpetual license to use, edit, publish, distribute, and promote all submitted assets, audio, likenesses, and social handles across all brand channels (including Instagram, YouTube, websites, and paid digital advertising) without additional compensation.

### 6. FTC Compliance & Social Media Disclaimer

**FTC Compliance:** Any posts published on Participant’s personal channels must clearly include required disclosures (e.g., #ad, #PBSpartner, or platform-native paid partnership tags).

**Platform Release:** This initiative is in no way sponsored, endorsed, administered by, or associated with Meta, Instagram, YouTube, or their parent companies. Participants release PBS, Comedy Seen, and hosting platforms from any and all liability or claims arising from participation.
`;

const footerLinks = [
    { "link": "/About", "label": "About" },
    { "link": "/Support", "label": "Support" },
    { "link": "/Pp", "label": "Privacy" },
    { "link": "/Tos", "label": "Terms of Use" },
];

export default function PawsBubblesAgreements() {
    const { classes } = useStyles();

    return (
        <>
            <Helmet>
                <title>Paws and Bubbles Spa – Submission Agreement | Comedy Seen</title>
                <meta name="description" content="Official promotion submission agreements and terms for Paws and Bubbles Spa partnership." />
            </Helmet>

            <Head />

            {/* Hero Header */}
            <Box className={classes.heroWrapper}>
                <Container size="md">
                    <Group position="center" spacing="xs" mb="xs">
                        <Badge size="lg" radius="sm" variant="gradient" gradient={{ from: 'blue', to: 'cyan' }}>
                            Partnership Promotion
                        </Badge>
                        <Badge size="lg" radius="sm" color="indigo" variant="outline">
                            Paws and Bubbles Spa Wheels
                        </Badge>
                    </Group>
                    <Text className={classes.pageTitle}>
                        Submission Agreement
                    </Text>
                    <Text className={classes.subtitle}>
                        Official rules, entry agreements, and terms for the Paws and Bubbles Spa promotional campaign.
                    </Text>
                </Container>
            </Box>

            {/* Main Content Area */}
            <Container size="md" my="xl">
                <Card className={classes.card} p="xl">
                    <Group spacing="sm" mb="md">
                        <IconFileText size={28} color="#0048a0" />
                        <Text className={classes.sectionTitle}>
                            Paws and Bubbles Spa Promotion Terms
                        </Text>
                    </Group>

                    <Divider mb="lg" />


                    <Box className={classes.prose}>
                        <ReactMarkdown>{agreementText}</ReactMarkdown>
                    </Box>

                    <Divider my="xl" />

                    <Group spacing="xs" position="center">
                        <IconShieldCheck size={20} color="#0048a0" />
                        <Text size="xs" color="dimmed">
                            Official Agreement • Comedy Seen x Paws and Bubbles Spa Partnership
                        </Text>
                    </Group>
                </Card>
                <Space h="xl" />
            </Container>

            <Footer links={footerLinks} />
        </>
    );
}